import Button from '@pinecast/common/Button';
import ErrorFlag from '@pinecast/common/icons/ErrorFlag';
import ScreenReaderOnly from '@pinecast/common/ScreenReaderOnly';
import TextArea from '@pinecast/common/TextArea';
import {FormattedMessage, useIntl} from '@pinecast/i18n';
import {useColors, useCSS} from '@pinecast/styles';
import * as React from 'react';

import type {UtteranceSaveStatus} from './types';

// How long the user must be idle before their edits are saved. Blur (leaving
// the field) always saves immediately, so this only bounds mid-edit exposure.
const SAVE_DEBOUNCE_MS = 2000;

type Props = {
  index: number;
  /** The `lang` of the text, in read mode and in the editor, for a page in
   * another language. A tag that a `lang` can have (`toLangTag`,
   * `langForText`), and not the language that a podcast has stored. */
  language?: string | null;
  text: string;
  onEdit?: (text: string, index: number, previousText: string) => Promise<void>;
  onSplit?: (previousText: string) => Promise<void>;
  onSaveStatusChange?: (
    editorId: string,
    status: UtteranceSaveStatus | null,
  ) => void;
};

const AutoSaveTextArea = ({
  label,
  language,
  value,
  onSave,
  onSplit,
  onSaveStatusChange,
}: {
  label: string;
  language?: string | null;
  value: string;
  onSave: (text: string, previousText: string) => Promise<void>;
  onSplit?: (previousText: string) => Promise<void>;
  onSaveStatusChange?: (
    editorId: string,
    status: UtteranceSaveStatus | null,
  ) => void;
}) => {
  const css = useCSS();
  const designColors = useColors();
  const editorId = React.useId();
  const labelId = React.useId();

  const [text, setText] = React.useState(value);
  const [status, setStatus] = React.useState<UtteranceSaveStatus | null>(null);
  const [splitPending, setSplitPending] = React.useState(false);

  const textRef = React.useRef(text);
  // The last text this editor knows the server accepted. It doubles as the
  // optimistic-concurrency token sent with each save.
  const lastSavedRef = React.useRef(value);
  const hasEverSavedRef = React.useRef(false);
  const saveInFlightRef = React.useRef<Promise<void> | null>(null);
  const debounceRef = React.useRef<number | null>(null);

  const onSaveRef = React.useRef(onSave);
  const onSplitRef = React.useRef(onSplit);
  const onSaveStatusChangeRef = React.useRef(onSaveStatusChange);
  React.useEffect(() => {
    onSaveRef.current = onSave;
    onSplitRef.current = onSplit;
    onSaveStatusChangeRef.current = onSaveStatusChange;
  });

  const clearDebounce = React.useCallback(() => {
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  // Saves are serialized: at most one request is in flight, and the loop
  // re-checks for newer text after each request so keystrokes made while a
  // save was in flight are never dropped.
  const flush = React.useCallback((): Promise<void> => {
    clearDebounce();
    if (saveInFlightRef.current) {
      return saveInFlightRef.current;
    }
    if (textRef.current === lastSavedRef.current) {
      return Promise.resolve();
    }
    const run = (async () => {
      try {
        while (textRef.current !== lastSavedRef.current) {
          const attempted = textRef.current;
          setStatus('saving');
          await onSaveRef.current(attempted, lastSavedRef.current);
          lastSavedRef.current = attempted;
          hasEverSavedRef.current = true;
        }
        setStatus(hasEverSavedRef.current ? 'saved' : null);
      } catch {
        setStatus('error');
      } finally {
        saveInFlightRef.current = null;
      }
    })();
    saveInFlightRef.current = run;
    return run;
  }, [clearDebounce]);

  const handleTextChange = React.useCallback(
    (newText: string) => {
      textRef.current = newText;
      setText(newText);
      clearDebounce();
      if (
        newText === lastSavedRef.current &&
        saveInFlightRef.current === null
      ) {
        // Edited back to the saved text; nothing left to save.
        setStatus(hasEverSavedRef.current ? 'saved' : null);
        return;
      }
      if (saveInFlightRef.current === null) {
        setStatus('unsaved');
      }
      debounceRef.current = window.setTimeout(() => {
        debounceRef.current = null;
        flush();
      }, SAVE_DEBOUNCE_MS);
    },
    [clearDebounce, flush],
  );

  // Adopt external updates (merges, splits, edits from elsewhere) only when
  // there is no unsaved local edit, so in-progress typing is never clobbered.
  React.useEffect(() => {
    if (
      saveInFlightRef.current === null &&
      textRef.current === lastSavedRef.current &&
      value !== textRef.current
    ) {
      textRef.current = value;
      setText(value);
    }
    lastSavedRef.current = value;
  }, [value]);

  React.useEffect(() => {
    onSaveStatusChangeRef.current?.(editorId, status);
  }, [editorId, status]);

  // On unmount: deregister from the aggregate status, and save any edits that
  // were still waiting on the debounce (e.g. switching back to reading mode).
  React.useEffect(
    () => () => {
      clearDebounce();
      if (textRef.current !== lastSavedRef.current) {
        flush();
      }
      onSaveStatusChangeRef.current?.(editorId, null);
    },
    [clearDebounce, editorId, flush],
  );

  const handleSplit = React.useCallback(() => {
    setSplitPending(true);
    flush()
      .then(() => {
        if (textRef.current !== lastSavedRef.current) {
          // The save failed; splitting now would operate on stale text.
          return;
        }
        return onSplitRef.current?.(lastSavedRef.current);
      })
      .catch(() => {})
      .finally(() => setSplitPending(false));
  }, [flush]);

  const canSplit = Boolean(onSplit) && /\n\n+/.test(text);

  return (
    <div>
      {/* The name is the interface's text, and the text area has the `lang` of
          the show. An `aria-label` on the text area is read with the voice of
          the show (WCAG 3.1.2), and it cannot have a `lang` of its own. So with
          a `lang`, the name is an element beside the text area, in the language
          of the page, and the text area points to it. */}
      {language ? (
        <ScreenReaderOnly id={labelId}>{label}</ScreenReaderOnly>
      ) : null}
      <TextArea
        {...(language ? {'aria-labelledby': labelId} : {'aria-label': label})}
        autoGrow
        lang={language ?? undefined}
        rows={1}
        value={text}
        onChange={handleTextChange}
        nativeEvents={{
          onBlur: () => {
            flush();
          },
        }}
      />
      {(status === 'error' || canSplit) && (
        <div
          className={css({
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            marginTop: '8px',
          })}
        >
          {status === 'error' ? (
            <>
              <span
                className={css({
                  alignItems: 'center',
                  color: designColors.contentText,
                  display: 'flex',
                  fontSize: 13,
                  gap: '6px',
                })}
              >
                <ErrorFlag color={designColors.borderRedAccent} size={14} />
                <FormattedMessage
                  description="Error shown under a transcript paragraph when saving it failed"
                  defaultMessage="This change couldn't be saved."
                />
              </span>
              <Button size="small" onClick={() => flush()}>
                <FormattedMessage
                  description="Label for the button that retries saving a transcript edit"
                  defaultMessage="Try again"
                />
              </Button>
            </>
          ) : (
            <Button size="small" onClick={handleSplit} pending={splitPending}>
              <FormattedMessage
                description="Label for the button that splits an utterance at paragraph breaks"
                defaultMessage="Split at paragraph break"
              />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

const TranscriptUtteranceEditor = ({
  text,
  index,
  language,
  onEdit,
  onSplit,
  onSaveStatusChange,
}: Props) => {
  const css = useCSS();
  const intl = useIntl();
  const handleSave = React.useCallback(
    (value: string, previousText: string) => {
      if (value !== previousText && onEdit) {
        return onEdit(value, index, previousText);
      }
      return Promise.resolve();
    },
    [index, onEdit],
  );
  return onEdit ? (
    <AutoSaveTextArea
      // The speaker menu beside it is "Line 12 speaker": with no visible
      // label, the number is what ties the two together.
      label={intl.formatMessage(
        {
          description:
            'Accessibility label for the text of one line of a transcript in the transcript editor. {line} is the number of the line, counting from 1.',
          defaultMessage: 'Line {line, number} text',
        },
        {line: index + 1},
      )}
      language={language}
      value={text}
      onSave={handleSave}
      onSplit={onSplit}
      onSaveStatusChange={onSaveStatusChange}
    />
  ) : (
    <p
      className={css({
        margin: 0,
        // Even a long unbroken string (an address, say) wraps rather than
        // making a narrow screen scroll sideways (WCAG 1.4.10).
        overflowWrap: 'anywhere',
      })}
      dir="auto"
      lang={language ?? undefined}
    >
      {addLineBreaks(text)}
    </p>
  );
};

export default TranscriptUtteranceEditor;

function addLineBreaks(text: string) {
  // Replace three or more line breaks in a row with two line breaks
  const normalized = text.replace(/\n{3,}/g, '\n\n');
  return normalized.split('\n').map((chunk, i) => (
    <React.Fragment key={i}>
      {Boolean(i) && <br />}
      {chunk}
    </React.Fragment>
  ));
}
